import './App.scss';
import * as Sentry from '@sentry/react';
import CircularProgress from '@mui/material/CircularProgress';
import React, { useEffect, useState } from 'react';
import {
  createBrowserRouter,
  RouterProvider,
  useLocation
} from 'react-router-dom';
import {
  globalStore,
  globalPersistor,
  ReduxProvider,
  PersistGate
} from './common/redux.Interface.Store';
import { GenericErrorBoundary } from './components/2compounds/GenericErrorBoundary';
import PreviewFooter from './components/3modules/Config/PreviewFooter';
import Sidebar from './components/5templates/Sidebar';
import AppRoutes from './routing/AppRoutes';
import { fetchLoginTheme } from './common/dynamicCustomizationService';
import { DataProvider } from './services/context/DataContext';
import GlobalStyles from './styling/GlobalStyles';

const ApplicationShell = () => {
  const { pathname } = useLocation();
  const onPreviewMode = localStorage.getItem('previewMode');
  const isLoginRoute = pathname.startsWith('/login');

  return (
    <>
      {!isLoginRoute && (
        <GenericErrorBoundary>
          <Sidebar />
        </GenericErrorBoundary>
      )}
      <AppRoutes />
      {!isLoginRoute && onPreviewMode && (
        <GenericErrorBoundary>
          <PreviewFooter />
        </GenericErrorBoundary>
      )}
    </>
  );
};

const createApplicationRouter = () =>
  createBrowserRouter(
    [
      {
        path: '*',
        element: <ApplicationShell />
      }
    ],
    {
      future: {
        v7_fetcherPersist: true,
        v7_normalizeFormMethod: true,
        v7_partialHydration: true,
        v7_relativeSplatPath: true,
        v7_skipActionErrorRevalidation: true
      }
    }
  );

const ApplicationRouter = () => {
  const [router] = useState(createApplicationRouter);

  useEffect(
    () => () => {
      router.dispose();
    },
    [router]
  );

  return (
    <RouterProvider router={router} future={{ v7_startTransition: true }} />
  );
};

function App() {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchLoginTheme()
      .then(() => {
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false); // can't get login theme? no big deal, life goes on with default theme, eat the error on purpose
      });
  }, []);

  return (
    <div className="App">
      <GlobalStyles />
      <ReduxProvider store={globalStore}>
        <PersistGate loading={null} persistor={globalPersistor}>
          <DataProvider>
            {isLoading && <CircularProgress />}
            {!isLoading && <ApplicationRouter />}
          </DataProvider>
        </PersistGate>
      </ReduxProvider>
    </div>
  );
}

export default Sentry.withProfiler(App);
